<template>
  <div class="contract-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractManage.list.title')" :subtitle="$t('contractManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="contractManageInfo.conditions.contractNameLike" clearable
            :placeholder="$t('contractManage.contractNamePlaceholder')" @keyup.enter.native="queryContractMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="contractManageInfo.conditions.contractCodeLike" clearable
            :placeholder="$t('contractManage.contractCodePlaceholder')" @keyup.enter.native="queryContractMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="contractManageInfo.conditions.contractType" clearable class="w-full"
            :placeholder="$t('contractManage.contractTypePlaceholder')">
            <el-option v-for="item in contractManageInfo.contractTypes" :key="item.contractTypeId" :label="item.typeName"
              :value="item.contractTypeId" />
          </el-select>
        </el-col>
        <el-col v-show="contractManageInfo.moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="contractManageInfo.conditions.partyBLike" clearable
            :placeholder="$t('contractManage.partyBPlaceholder')" @keyup.enter.native="queryContractMethod" />
        </el-col>
        <el-col v-show="contractManageInfo.moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="contractManageInfo.conditions.queryStartTime" type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss" class="w-full" :placeholder="$t('contractManage.startTimePlaceholder')" />
        </el-col>
        <el-col v-show="contractManageInfo.moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="contractManageInfo.conditions.queryEndTime" type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss" class="w-full" :placeholder="$t('contractManage.endTimePlaceholder')" />
        </el-col>
        <el-col v-show="contractManageInfo.moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="contractManageInfo.conditions.parentContractCodeLike" clearable
            :placeholder="$t('contractManage.parentContractCodePlaceholder')" @keyup.enter.native="queryContractMethod" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryContractMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetContractMethod">
          {{ $t('common.reset') }}
        </el-button>
        <el-button type="text" icon="el-icon-more" @click="toggleMoreCondition">
          {{ contractManageInfo.moreCondition ? $t('common.hide') : $t('common.more') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="listContracts">
        <template #left>
          <span class="ds-panel__title">{{ $t('contractManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="contractManageInfo.contracts" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('contractManage.page.empty')" @retry="listContracts">
        <el-table-column prop="contractName" :label="$t('contractManage.table.contractName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="contractCode" :label="$t('contractManage.table.contractCode')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.contractCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="parentContractCode" :label="$t('contractManage.table.parentContractCode')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.parentContractCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="contractTypeName" :label="$t('contractManage.table.contractType')" align="center" width="120" />
        <el-table-column prop="operator" :label="$t('contractManage.table.operator')" align="center" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.operator }}({{ scope.row.operatorLink }})
          </template>
        </el-table-column>
        <el-table-column prop="amount" :label="$t('contractManage.table.amount')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="partyB" :label="$t('contractManage.table.partyB')" align="center" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.partyB }}({{ scope.row.bLink }})
          </template>
        </el-table-column>
        <el-table-column :label="$t('contractManage.table.validityPeriod')" align="center" width="200">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.startTime }}</span><br /><span class="ds-num">~{{ scope.row.endTime }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('contractManage.table.createTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('contractManage.table.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('contractManage.table.operation')" align="center" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="viewContract(scope.row)">
              {{ $t('contractManage.operation.view') }}
            </el-button>
            <el-button size="mini" type="text" @click="printContract(scope.row)">
              {{ $t('contractManage.operation.print') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteContractModel(scope.row)">
              {{ $t('contractManage.operation.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 子组件 -->
    <edit-contract ref="editContract" @success="handleSuccess" />
    <delete-contract ref="deleteContract" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryContract, queryContractType } from '@/api/contract/contractManageApi'
import EditContract from '@/components/contract/editContract'
import DeleteContract from '@/components/contract/deleteContract'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ContractManageList',
  components: {
    EditContract,
    DeleteContract,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      contractManageInfo: {
        contracts: [],
        moreCondition: false,
        conditions: {
          contractNameLike: '',
          contractCodeLike: '',
          contractType: '',
          partyBLike: '',
          queryStartTime: '',
          queryEndTime: '',
          parentContractCodeLike: ''
        },
        contractTypes: []
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.listContracts()
    this.listContractTypes()
  },
  methods: {
    formatMoney(value) {
      return formatMoney(value)
    },
    async listContracts() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.contractManageInfo.conditions
        }
        const { data, total } = await queryContract(params)
        this.contractManageInfo.contracts = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取合同列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('contractManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    async listContractTypes() {
      try {
        const params = {
          page: 1,
          row: 50
        }
        const { data } = await queryContractType(params)
        this.contractManageInfo.contractTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取合同类型失败:', error)
      }
    },
    toggleMoreCondition() {
      this.contractManageInfo.moreCondition = !this.contractManageInfo.moreCondition
    },
    queryContractMethod() {
      this.page.current = 1
      this.listContracts()
    },
    resetContractMethod() {
      this.contractManageInfo.conditions = {
        contractNameLike: '',
        contractCodeLike: '',
        contractType: '',
        partyBLike: '',
        queryStartTime: '',
        queryEndTime: '',
        parentContractCodeLike: ''
      }
      this.page.current = 1
      this.listContracts()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.listContracts()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.listContracts()
    },
    openContractFee(contract) {
      this.$router.push({
        path: '/property/listContractFee',
        query: {
          contractId: contract.contractId,
          contractCode: contract.contractCode
        }
      })
    },
    viewContract(contract) {
      this.$router.push({
        path: '/views/contract/contractDetail',
        query: {
          contractId: contract.contractId
        }
      })
    },
    printContract(contract) {
      window.open(`/#/views/contract/printContract?contractTypeId=${contract.contractType}&contractId=${contract.contractId}`)
    },
    openDeleteContractModel(contract) {
      this.$refs.deleteContract.open(contract)
    },
    handleSuccess() {
      this.listContracts()
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
